export const DEFAULT_MIXPANEL_TOKEN = 'b9e5f9bcabaf1e2c02dc84b809466e00';

export const MIXPANEL_SCRIPT_ID = 'onbuoy-mixpanel-sdk';
export const MIXPANEL_SCRIPT_URL = 'https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js';

const MIXPANEL_METHODS = [
  'disable',
  'time_event',
  'track',
  'track_pageview',
  'track_links',
  'track_forms',
  'track_with_groups',
  'add_group',
  'set_group',
  'remove_group',
  'register',
  'register_once',
  'alias',
  'unregister',
  'identify',
  'name_tag',
  'set_config',
  'reset',
  'opt_in_tracking',
  'opt_out_tracking',
  'start_session_recording',
  'stop_session_recording',
];

let initialized = false;
let identifiedUserId = null;
let identifiedProfileFingerprint = null;
let lastTrackedPagePath = null;

const logDevelopmentWarning = (message, error) => {
  if (process.env.NODE_ENV === 'development') {
    // Do not include analytics properties or user data in logs.
    // eslint-disable-next-line no-console
    console.warn(`[Mixpanel] ${message}`, error || '');
  }
};

const addQueuedMethod = (target, methodName) => {
  target[methodName] = function queuedMixpanelMethod() {
    target.push([methodName].concat(Array.prototype.slice.call(arguments, 0)));
  };
};

// This is the queueing behavior from Mixpanel's official browser snippet. It
// lets calls made before the async SDK finishes loading execute in order.
const createMixpanelStub = () => {
  const mixpanel = [];
  mixpanel._i = [];
  mixpanel.__SV = 1.2;
  mixpanel.people = [];
  MIXPANEL_METHODS.forEach(methodName => addQueuedMethod(mixpanel, methodName));
  ['set', 'set_once', 'unset', 'increment', 'append', 'union', 'remove'].forEach(methodName =>
    addQueuedMethod(mixpanel.people, methodName)
  );
  mixpanel.init = (token, config, name) => {
    mixpanel._i.push([token, config, name]);
  };
  return mixpanel;
};

const mixpanelToken = token => {
  const configuredToken = token || process.env.REACT_APP_MIXPANEL_TOKEN || DEFAULT_MIXPANEL_TOKEN;
  return typeof configuredToken === 'string' ? configuredToken.trim() : '';
};

const mixpanelInitConfig = {
  // Autocapture stays enabled, but pageviews use OnBuoy's canonical route
  // middleware so query strings are not captured and events are not duplicated.
  autocapture: {
    pageview: false,
    capture_text_content: false,
    block_selectors: [
      'input[type="password"]',
      'input[type="file"]',
      'textarea[name="message"]',
      'textarea[name="content"]',
      'iframe',
      '.StripeElement',
      '[data-mixpanel-sensitive]',
    ],
  },
  record_sessions_percent: 100,
  // Keep the SDK's privacy-first defaults explicit. Rendered text and all
  // inputs are masked, and sensitive media/embeds are absent from replays.
  record_mask_all_text: true,
  record_mask_all_inputs: true,
  record_block_selector:
    'img, video, audio, iframe, input[type="file"], .StripeElement, [data-mixpanel-sensitive]',
};

export const initializeMixpanel = token => {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return false;
    }

    const projectToken = mixpanelToken(token);
    if (!projectToken) {
      return false;
    }

    if (initialized || window['__ONBUOY_MIXPANEL_INITIALIZED__']) {
      initialized = true;
      return true;
    }

    window.mixpanel = window.mixpanel || createMixpanelStub();
    window.mixpanel.init(projectToken, mixpanelInitConfig);
    initialized = true;
    window['__ONBUOY_MIXPANEL_INITIALIZED__'] = true;

    if (!document.getElementById(MIXPANEL_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = MIXPANEL_SCRIPT_ID;
      script.type = 'text/javascript';
      script.async = true;
      script.src = MIXPANEL_SCRIPT_URL;
      script.onerror = error => {
        logDevelopmentWarning('The browser SDK could not be loaded.', error);
      };
      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.head.appendChild(script);
      }
    }

    return true;
  } catch (error) {
    logDevelopmentWarning('Initialization failed safely.', error);
    return false;
  }
};

const getMixpanel = () => {
  return typeof window !== 'undefined' ? window.mixpanel : null;
};

export const trackEvent = (eventName, properties = {}) => {
  try {
    if (typeof eventName !== 'string' || !eventName.trim()) {
      return false;
    }

    const mixpanel = getMixpanel();
    if (!mixpanel || typeof mixpanel.track !== 'function') {
      return false;
    }

    mixpanel.track(eventName.trim(), properties || {});
    return true;
  } catch (error) {
    logDevelopmentWarning('Event tracking failed safely.', error);
    return false;
  }
};

export const trackPageView = (properties = {}) => {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return false;
    }

    const pagePath =
      typeof properties.page_path === 'string' && properties.page_path
        ? properties.page_path
        : window.location.pathname;
    if (!pagePath || pagePath === lastTrackedPagePath) {
      return false;
    }

    const mixpanel = getMixpanel();
    if (!mixpanel || typeof mixpanel.track_pageview !== 'function') {
      return false;
    }

    const pageUrl = new URL(pagePath, window.location.origin).href;
    mixpanel.track_pageview({
      ...properties,
      page_path: pagePath,
      page_url: properties.page_url || pageUrl,
      page_title: properties.page_title || document.title,
    });
    lastTrackedPagePath = pagePath;
    return true;
  } catch (error) {
    logDevelopmentWarning('Page-view tracking failed safely.', error);
    return false;
  }
};

const userId = user => {
  const id = user?.id?.uuid || user?.id;
  return typeof id === 'string' ? id.trim() : '';
};

const userProfileProperties = user => {
  const profile = user?.attributes?.profile || {};
  const publicData = profile.publicData || {};
  const properties = {
    $email: user?.attributes?.email,
    $name: profile.displayName || [profile.firstName, profile.lastName].filter(Boolean).join(' '),
    role: publicData.userType || publicData.role,
  };

  return Object.entries(properties).reduce((filtered, [key, value]) => {
    return value == null || value === '' ? filtered : { ...filtered, [key]: value };
  }, {});
};

export const identifyUser = user => {
  try {
    const id = userId(user);
    const mixpanel = getMixpanel();
    if (!id || !mixpanel || typeof mixpanel.identify !== 'function') {
      return false;
    }

    if (identifiedUserId !== id) {
      mixpanel.identify(id);
      identifiedUserId = id;
      identifiedProfileFingerprint = null;
    }

    const profileProperties = userProfileProperties(user);
    const profileFingerprint = JSON.stringify(profileProperties);
    if (
      Object.keys(profileProperties).length > 0 &&
      identifiedProfileFingerprint !== profileFingerprint &&
      typeof mixpanel.people?.set === 'function'
    ) {
      mixpanel.people.set(profileProperties);
      identifiedProfileFingerprint = profileFingerprint;
    }

    return true;
  } catch (error) {
    logDevelopmentWarning('User identification failed safely.', error);
    return false;
  }
};

export const resetMixpanel = () => {
  try {
    const mixpanel = getMixpanel();
    if (!mixpanel || typeof mixpanel.reset !== 'function') {
      return false;
    }

    mixpanel.reset();
    identifiedUserId = null;
    identifiedProfileFingerprint = null;
    return true;
  } catch (error) {
    logDevelopmentWarning('Identity reset failed safely.', error);
    return false;
  }
};

export const connectMixpanelToStore = store => {
  if (!store || typeof store.getState !== 'function' || typeof store.subscribe !== 'function') {
    return () => {};
  }

  let previousUserId = null;
  const syncIdentity = () => {
    const currentUser = store.getState()?.user?.currentUser;
    const currentUserId = userId(currentUser);

    if (currentUserId) {
      if (previousUserId && previousUserId !== currentUserId) {
        resetMixpanel();
      }
      identifyUser(currentUser);
    } else if (previousUserId) {
      resetMixpanel();
    }
    previousUserId = currentUserId || null;
  };

  syncIdentity();
  return store.subscribe(syncIdentity);
};

export const resetMixpanelTestState = () => {
  initialized = false;
  identifiedUserId = null;
  identifiedProfileFingerprint = null;
  lastTrackedPagePath = null;
};
